import { doc, getDoc, getDocs, collection, query, where, orderBy, limit, startAfter, DocumentSnapshot } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { getFunctions, HttpsCallableResult } from 'firebase/functions'
import { httpsCallable } from '@/services/callable'
import { knowledgeBaseService } from './knowledgeBase'
import type {
  VulnerabilityListResponse,
  VulnerabilitySummary,
  AgentListResponse,
  CyberEssentialsReport,
  User,
  Vulnerability,
  Agent,
} from '@types'

// Check if demo mode is enabled
const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true'

const functions = getFunctions(undefined, 'europe-west2')

/**
 * Firestore service for reading cached Qualys data
 * All reads come directly from Firestore tag-based collections
 */
export const firestoreService = {
  /**
   * Get vulnerabilities for a tag from Firestore cache
   * Supports cursor-based pagination for efficient loading of large datasets
   */
  async getVulnerabilities(tagId: string, filters?: {
    status?: string[]
    severityMin?: number
    limitCount?: number
    startAfterDoc?: DocumentSnapshot // Cursor for pagination
  }): Promise<VulnerabilityListResponse> {
    const vulnsRef = collection(db, 'tags', tagId, 'vulnerabilities')

    let q = query(vulnsRef, orderBy('severity', 'desc'), orderBy('lastFound', 'desc'))

    // Apply filters if provided
    if (filters?.status && filters.status.length > 0) {
      q = query(q, where('status', 'in', filters.status))
    }

    // Pagination: start after cursor if provided
    if (filters?.startAfterDoc) {
      q = query(q, startAfter(filters.startAfterDoc))
    }

    // Fetch one extra to determine if there are more results
    const pageSize = filters?.limitCount || 1000 // Default to 1000 if not specified
    const fetchSize = filters?.limitCount ? pageSize + 1 : pageSize
    q = query(q, limit(fetchSize))

    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      return {
        vulnerabilities: [],
        totalCount: 0,
        page: 1,
        pageSize: pageSize,
        hasMore: false,
      }
    }

    // Determine pagination metadata
    const docs = snapshot.docs
    const hasMore = filters?.limitCount ? docs.length > pageSize : false
    const docsToProcess = hasMore ? docs.slice(0, pageSize) : docs
    const lastDoc = hasMore ? docsToProcess[docsToProcess.length - 1] : docs[docs.length - 1]

    // Extract vulnerability detections from Firestore
    const vulnerabilities: Vulnerability[] = docsToProcess.map(doc => {
      const data = doc.data()
      return {
        id: doc.id,
        qid: parseInt(data.qid) || 0,
        title: data.title || `Vulnerability QID ${data.qid}`, // Will be enriched with KB data if available
        hostId: data.hostId,
        ipAddress: data.hostIp, // Map hostIp to ipAddress
        hostname: data.hostname,
        severity: data.severity,
        status: data.status,
        results: data.results,
        firstDetected: data.firstFound?.toDate ? data.firstFound.toDate() : data.firstFound, // Map firstFound to firstDetected
        lastDetected: data.lastFound?.toDate ? data.lastFound.toDate() : data.lastFound, // Map lastFound to lastDetected
        timesFound: data.timesFound,
        type: data.type,
        port: data.port,
        protocol: data.protocol,
        category: data.category,
        patchable: data.patchable,
        cvssScore: data.cvssScore,
        cvssV3Score: data.cvssV3Score,
      } as Vulnerability
    })

    // Fetch Knowledge Base data for all QIDs to enrich with titles and metadata
    try {
      const uniqueQids = [...new Set(vulnerabilities.map(v => v.qid))]
      const kbData = await knowledgeBaseService.getVulnerabilitiesByQIDs(uniqueQids)

      // Debug logging
      const missingKbQids: number[] = []

      // Enrich vulnerabilities with KB data
      vulnerabilities.forEach(vuln => {
        const kb = kbData.get(String(vuln.qid))
        if (kb) {
          // Only overwrite if KB has actual data (prevents undefined overwrites)
          if (kb.title) vuln.title = kb.title
          if (kb.category) vuln.category = kb.category
          if (kb.patchable !== undefined) vuln.patchable = kb.patchable
          if (kb.diagnosis) vuln.diagnosis = kb.diagnosis
          if (kb.solution) vuln.solution = kb.solution
          if (kb.cveList) vuln.cveList = kb.cveList
          if (kb.cvssVector) vuln.cvssVector = kb.cvssVector
          if (kb.cvssV3Vector) vuln.cvssV3Vector = kb.cvssV3Vector
          // Prefer detection-level CVSS if already present, otherwise use KB's
          if (!vuln.cvssScore && kb.cvssScore) {
            vuln.cvssScore = kb.cvssScore
          }
          if (!vuln.cvssV3Score && kb.cvssV3Score) {
            vuln.cvssV3Score = kb.cvssV3Score
          }
        } else {
          // Track QIDs without KB data
          missingKbQids.push(vuln.qid)
        }
      })

      // Log missing KB data for debugging
      if (missingKbQids.length > 0) {
        console.warn(`KB data missing for ${missingKbQids.length}/${uniqueQids.length} QIDs:`, missingKbQids.slice(0, 10))
      }
    } catch (error) {
      // If KB fetch fails, continue with detection data only
      console.error('Failed to fetch KB data for vulnerabilities:', error)
    }

    // Filter by severity if needed
    const filteredVulns = filters?.severityMin
      ? vulnerabilities.filter(v => v.severity >= filters.severityMin!)
      : vulnerabilities

    return {
      vulnerabilities: filteredVulns,
      totalCount: filteredVulns.length,
      page: 1,
      pageSize: pageSize,
      hasMore,
      lastDocId: lastDoc?.id,
    }
  },

  /**
   * Get vulnerability summary from cached data or pre-fetched vulnerabilities
   * @param tagIdOrVulns - Either a tag ID to fetch vulnerabilities, or pre-fetched VulnerabilityListResponse
   */
  async getVulnerabilitySummary(tagIdOrVulns: string | VulnerabilityListResponse): Promise<VulnerabilitySummary> {
    // If passed a string, fetch vulnerabilities. Otherwise use the provided data.
    const vulns = typeof tagIdOrVulns === 'string'
      ? await this.getVulnerabilities(tagIdOrVulns)
      : tagIdOrVulns

    const summary: VulnerabilitySummary = {
      totalCount: vulns.totalCount,
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      info: 0,
      byStatus: {
        NEW: 0,
        ACTIVE: 0,
        REOPENED: 0,
        FIXED: 0,
      },
    }

    vulns.vulnerabilities.forEach(vuln => {
      // Count by severity
      switch (vuln.severity) {
        case 5:
          summary.critical++
          break
        case 4:
          summary.high++
          break
        case 3:
          summary.medium++
          break
        case 2:
          summary.low++
          break
        case 1:
          summary.info++
          break
      }

      // Count by status
      if (summary.byStatus[vuln.status] !== undefined) {
        summary.byStatus[vuln.status]++
      }
    })

    return summary
  },

  /**
   * Get hosts/agents for a tag from Firestore cache
   */
  async getHosts(tagId: string, filters?: {
    agentStatus?: string
    limitCount?: number
  }): Promise<AgentListResponse> {
    const hostsRef = collection(db, 'tags', tagId, 'hosts')

    let q = query(hostsRef, orderBy('lastActivity', 'desc'))

    if (filters?.agentStatus) {
      q = query(q, where('agentStatus', '==', filters.agentStatus))
    }

    if (filters?.limitCount) {
      q = query(q, limit(filters.limitCount))
    }

    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      return {
        agents: [],
        totalCount: 0,
      }
    }

    const agents: Agent[] = snapshot.docs.map(doc => {
      const data = doc.data()

      // Parse platform from OS string
      let platform: 'windows' | 'linux' | 'mac' = 'linux'
      const osLower = (data.os || '').toLowerCase()
      if (osLower.includes('windows')) {
        platform = 'windows'
      } else if (osLower.includes('mac') || osLower.includes('darwin')) {
        platform = 'mac'
      }

      // Determine status from agentStatus
      let status: 'active' | 'inactive' | 'error' = 'inactive'
      if (data.agentStatus === 'RUNNING' || data.agentStatus === 'ACTIVE') {
        status = 'active'
      } else if (data.agentStatus === 'ERROR') {
        status = 'error'
      }

      return {
        id: doc.id,
        hostId: data.id || doc.id,
        hostname: data.hostname || data.fqdn || data.ip || 'Unknown',
        ipAddress: data.ip || 'Unknown',
        platform,
        osVersion: data.os || 'Unknown',
        agentVersion: data.agentVersion || 'Qualys Cloud Agent',
        status,
        lastCheckin: data.lastActivity?.toDate() || new Date(),
        activatedAt: data.firstFoundDate?.toDate() || new Date(),
        tagId: tagId,
      } as Agent
    })

    return {
      agents,
      totalCount: agents.length,
    }
  },

  /**
   * Get Cyber Essentials report (not yet migrated to new structure)
   * This will need to be updated once CE data is synced to organization subcollections
   */
  async getCyberEssentialsReport(tagId: string): Promise<CyberEssentialsReport> {
    // For now, keep reading from old structure
    // TODO: Migrate to organization subcollections when CE sync is implemented
    const docRef = doc(db, 'cyberEssentials', tagId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      throw new Error('Cyber Essentials data not yet available. Please check back later.')
    }

    const data = docSnap.data()

    if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
      console.warn('Cached CE data is expired')
    }

    return data.data as CyberEssentialsReport
  },

  /**
   * Get tag metadata including lastSyncAt timestamp and pre-calculated summary
   */
  async getTagMetadata(tagId: string): Promise<{
    lastSyncAt: Date | null
    summary?: VulnerabilitySummary
  }> {
    const docRef = doc(db, 'tags', tagId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return { lastSyncAt: null }
    }

    const data = docSnap.data()
    return {
      lastSyncAt: data.lastSyncAt?.toDate ? data.lastSyncAt.toDate() : null,
      summary: data.summary, // Pre-calculated summary from Cloud Functions
    }
  },

  /**
   * Get a vulnerability document snapshot by ID (for pagination cursor)
   */
  async getVulnerabilityDoc(tagId: string, docId: string): Promise<DocumentSnapshot | null> {
    const docRef = doc(db, 'tags', tagId, 'vulnerabilities', docId)
    const docSnap = await getDoc(docRef)
    return docSnap.exists() ? docSnap : null
  },

  /**
   * Get user data from Firestore
   */
  async getUser(uid: string): Promise<User | null> {
    // Demo mode: return demo user data
    if (isDemoMode) {
      const { demoUser, DEMO_TAG_ID } = await import('@/data/demo/demoData')
      return {
        uid: demoUser.uid,
        email: demoUser.email,
        role: demoUser.role,
        organizationId: DEMO_TAG_ID,
        allowedTags: demoUser.allowedTags,
        createdAt: demoUser.createdAt,
        updatedAt: demoUser.updatedAt,
      } as User
    }

    const docRef = doc(db, 'users', uid)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return null
    }

    const data = docSnap.data()
    return {
      ...data,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
    } as User
  },

  /**
   * Get all users (admin only - enforced by security rules)
   */
  async getAllUsers(): Promise<User[]> {
    // Demo mode: return demo user list
    if (isDemoMode) {
      const { demoUser, DEMO_TAG_ID } = await import('@/data/demo/demoData')
      return [{
        uid: demoUser.uid,
        email: demoUser.email,
        role: demoUser.role,
        organizationId: DEMO_TAG_ID,
        allowedTags: demoUser.allowedTags,
        createdAt: demoUser.createdAt,
        updatedAt: demoUser.updatedAt,
      } as User]
    }

    const usersRef = collection(db, 'users')
    const snapshot = await getDocs(usersRef)

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as User[]
  },

  /**
   * Manually trigger data refresh for an organization
   * Calls Cloud Function to trigger full Qualys sync
   */
  async refreshOrganizationData(organizationId: string): Promise<{
    success: boolean
    hostsWritten: number
    vulnerabilitiesWritten: number
    vulnerabilitiesFixed: number
    duration: number
  }> {
    const refreshFn = httpsCallable(functions, 'triggerQualysSync')
    const result = await refreshFn({ organizationId }) as HttpsCallableResult<{
      success: boolean
      hostsWritten: number
      vulnerabilitiesWritten: number
      vulnerabilitiesFixed: number
      duration: number
    }>

    return result.data
  },
}

/**
 * Admin functions - these call Cloud Functions instead of backend API
 */
export const adminService = {
  /**
   * Create a new user
   */
  async createUser(params: {
    email: string
    password: string
    tagId: string
    role: 'user' | 'admin'
    activationKey?: string
    activationKeyTitle?: string
  }): Promise<{ success: boolean; uid: string; email: string }> {
    const createUserFn = httpsCallable(functions, 'adminCreateUser')
    const result = await createUserFn(params) as HttpsCallableResult<{ success: boolean; uid: string; email: string }>

    return result.data
  },

  /**
   * Update an existing user
   */
  async updateUser(params: {
    uid: string
    email?: string
    password?: string
    tagId?: string
    role?: 'user' | 'admin'
  }): Promise<{ success: boolean }> {
    const updateUserFn = httpsCallable(functions, 'adminUpdateUser')
    const result = await updateUserFn(params) as HttpsCallableResult<{ success: boolean }>

    return result.data
  },

  /**
   * Delete a user
   */
  async deleteUser(uid: string): Promise<{ success: boolean }> {
    const deleteUserFn = httpsCallable(functions, 'adminDeleteUser')
    const result = await deleteUserFn({ uid }) as HttpsCallableResult<{ success: boolean }>

    return result.data
  },

  /**
   * List all users with optional filtering
   */
  async listUsers(params: {
    limit?: number
    offset?: number
    search?: string
    role?: 'user' | 'admin'
  }): Promise<{ users: User[]; total: number }> {
    const listUsersFn = httpsCallable(functions, 'adminListUsers')
    const result = await listUsersFn(params) as HttpsCallableResult<{ users: any[]; total: number }>

    // Convert Firestore timestamps to Date objects
    const users = result.data.users.map(user => ({
      ...user,
      createdAt: user.createdAt?._seconds ? new Date(user.createdAt._seconds * 1000) : user.createdAt,
      updatedAt: user.updatedAt?._seconds ? new Date(user.updatedAt._seconds * 1000) : user.updatedAt,
    }))

    return {
      users: users as User[],
      total: result.data.total,
    }
  },

  /**
   * Get a specific user by UID
   */
  async getUserByUid(uid: string): Promise<User> {
    const getUserFn = httpsCallable(functions, 'adminGetUser')
    const result = await getUserFn({ uid }) as HttpsCallableResult<any>

    const user = result.data
    return {
      ...user,
      createdAt: user.createdAt?._seconds ? new Date(user.createdAt._seconds * 1000) : user.createdAt,
      updatedAt: user.updatedAt?._seconds ? new Date(user.updatedAt._seconds * 1000) : user.updatedAt,
    } as User
  },
}
